<template>
  <div class="aso-upload-face-card" :class="{ 'aso-upload-face-card--disabled': disabled }">
    <p v-if="label" class="aso-upload-face-card__label">{{ label }}</p>

    <div class="aso-upload-face-card__trigger" ref="triggerRef">
      <button
        type="button"
        class="aso-upload-face-card__visual"
        :disabled="disabled || busy"
        @click="tryOpenLiveCamera"
      >
        <span v-if="loading" class="aso-upload-face-card__loader">Falta pouco...</span>
        <span v-else-if="uploaded" class="aso-upload-face-card__status aso-upload-face-card__status--success">✓ Foto validada</span>
        <span v-else-if="hasError" class="aso-upload-face-card__status aso-upload-face-card__status--error">⚠ Falha no upload</span>
        <span v-else class="aso-upload-face-card__status">Toque para tirar foto</span>
      </button>
    </div>

    <p v-if="cameraError && !isWebcamOpen" class="aso-upload-face-card__camera-error aso-upload-face-card__camera-error--inline">
      {{ cameraError }}
    </p>

    <!-- Modal único com estados de câmera e preview -->
    <div
      v-if="isWebcamOpen"
      class="aso-upload-face-card__modal"
      :class="capturedPhoto ? 'aso-upload-face-card__modal--preview' : 'aso-upload-face-card__modal--camera'"
    >
      <div
        class="aso-upload-face-card__modal-content"
        :class="capturedPhoto ? 'aso-upload-face-card__modal-content--preview' : 'aso-upload-face-card__modal-content--camera'"
      >
        <h3 class="aso-upload-face-card__modal-title">{{ capturedPhoto ? 'Confirme sua foto' : 'Tire sua foto' }}</h3>

        <div v-show="capturedPhoto" class="aso-upload-face-card__preview-container">
          <img :src="capturedPhoto" class="aso-upload-face-card__preview-full" />
        </div>

        <div v-show="!capturedPhoto" class="aso-upload-face-card__media-container">
          <video
            ref="videoElement"
            class="aso-upload-face-card__video"
            autoplay
            playsinline
          ></video>
        </div>

        <div
          class="aso-upload-face-card__modal-actions"
          :class="capturedPhoto ? 'aso-upload-face-card__modal-actions--preview' : 'aso-upload-face-card__modal-actions--camera'"
        >
          <button
            v-if="capturedPhoto"
            type="button"
            class="aso-upload-face-card__modal-btn aso-upload-face-card__modal-btn--primary"
            :disabled="busy"
            @click="confirmPhoto"
          >
            Confirmar e enviar
          </button>

          <button
            v-else
            type="button"
            class="aso-upload-face-card__modal-btn aso-upload-face-card__modal-btn--primary"
            :disabled="busy"
            @click="captureFrame"
          >
            Tirar foto
          </button>

          <button
            type="button"
            class="aso-upload-face-card__modal-btn aso-upload-face-card__modal-btn--danger"
            :disabled="busy"
            @click="capturedPhoto ? resetCapture() : closeLiveCamera()"
          >
            {{ capturedPhoto ? 'Tirar outra foto' : 'Cancelar' }}
          </button>
        </div>

        <p v-if="cameraError && !capturedPhoto" class="aso-upload-face-card__camera-error">{{ cameraError }}</p>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'UploadFaceCard',
  props: {
    label: {
      type: String,
      default: '',
    },
    busy: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    uploaded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isWebcamOpen: false,
      cameraError: '',
      capturedPhoto: null,
      mediaStream: null,
    }
  },
  beforeDestroy() {
    this.stopCamera()
  },
  methods: {
    async tryOpenLiveCamera() {
      if (this.disabled || this.busy) {
        return
      }
      this.cameraError = ''

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user' },
          audio: false,
        })

        this.mediaStream = stream
        this.isWebcamOpen = true

        this.$nextTick(() => {
          this.attachStreamToVideo()
        })
      } catch (error) {
        this.cameraError = 'Não foi possível acessar a câmera. Verifique as permissões do navegador.'
        console.error('Erro ao acessar câmera:', error)
      }
    },
    captureFrame() {
      const video = this.$refs.videoElement
      if (!video) {
        return
      }

      const canvas = document.createElement('canvas')
      canvas.width = video.videoWidth || 640
      canvas.height = video.videoHeight || 480

      const ctx = canvas.getContext('2d')
      ctx.drawImage(video, 0, 0)

      this.capturedPhoto = canvas.toDataURL('image/jpeg', 0.95)
    },
    async confirmPhoto() {
      if (!this.capturedPhoto) {
        return
      }

      // Converter data URL para Blob/File
      const response = await fetch(this.capturedPhoto)
      const blob = await response.blob()
      const file = new File([blob], 'selfie.jpg', { type: 'image/jpeg' })

      this.$emit('selected', file)
      this.closeLiveCamera()
    },
    resetCapture() {
      this.capturedPhoto = null

      // Garantir que o vídeo volta ao vivo
      this.$nextTick(() => {
        this.attachStreamToVideo()
      })
    },
    attachStreamToVideo() {
      const video = this.$refs.videoElement
      if (!video || !this.mediaStream) {
        return
      }

      if (video.srcObject !== this.mediaStream) {
        video.srcObject = this.mediaStream
      }

      const playPromise = video.play && video.play()
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {})
      }
    },
    closeLiveCamera() {
      this.stopCamera()
      this.isWebcamOpen = false
      this.capturedPhoto = null
      this.cameraError = ''
    },
    stopCamera() {
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach((track) => track.stop())
        this.mediaStream = null
      }
    },
  },
}
</script>

<style scoped>
.aso-upload-face-card {
  --branco: #ffffff;
  --background-50: #f8fafb;
  --brancos-100: #dddfe0;
  --azul-claro: #ecf5fc;
  --azul-50: #0575e6;
  --azul-100: #02298a;
  --azul-300: #002855;
  --cinza-100: #7d8597;
  --cinza-300: #33415c;
  --vermelho: #f04438;
  font-family: "Poppins", "Helvetica Neue", Arial, sans-serif;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
  opacity: 1;
  transition: opacity 0.3s ease;
  position: relative;
}

.aso-upload-face-card--disabled {
  opacity: 0.5;
  pointer-events: none;
}

.aso-upload-face-card__label {
  font-weight: 600;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--cinza-100);
  margin: 0;
}

.aso-upload-face-card__visual {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 140px;
  background-color: var(--background-50);
  border: 1px solid var(--brancos-100);
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: var(--cinza-300) !important;
  transition: all 0.3s ease;
  padding: 16px;
  text-align: center;
}

.aso-upload-face-card__visual:hover:not(:disabled) {
  border-color: #8ac0f2;
  background-color: #f3f9ff;
  box-shadow: 0 0 0 3px rgba(5, 117, 230, 0.12);
}

.aso-upload-face-card__visual:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.aso-upload-face-card__loader {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    opacity: 1;
  }

  50% {
    opacity: 0.5;
  }

  100% {
    opacity: 1;
  }
}

.aso-upload-face-card__status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.aso-upload-face-card__status--success {
  color: #1d6f55;
  font-weight: 700;
}

.aso-upload-face-card__status--error {
  color: var(--vermelho);
  font-weight: 700;
}

.aso-upload-face-card__trigger {
  position: relative;
  width: 100%;
  max-width: 320px;
  align-self: center;
  justify-self: center;
}

.aso-upload-face-card__trigger .aso-upload-face-card__visual {
  width: 100%;
}

/* Modal da câmera ao vivo */
.aso-upload-face-card__modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
}

.aso-upload-face-card__modal-content {
  background-color: var(--branco);
  border-radius: 12px;
  max-width: 500px;
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  box-shadow: 0 12px 28px rgba(0, 24, 69, 0.24);
}

.aso-upload-face-card__video {
  width: 100%;
  max-width: 500px;
  max-height: 400px;
  background-color: #000;
  display: block;
}

.aso-upload-face-card__preview-section {
  display: none;
}

.aso-upload-face-card__preview {
  max-width: 100%;
  max-height: 300px;
  border-radius: 8px;
  border: 1px solid #ddd;
}

.aso-upload-face-card__modal-actions {
  display: flex;
  gap: 8px;
  padding: 16px;
  border-top: 1px solid var(--brancos-100);
  flex-wrap: wrap;
  justify-content: center;
}

.aso-upload-face-card__modal-btn {
  flex: 1;
  min-width: 100px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid var(--brancos-100);
  border-radius: 6px;
  background-color: var(--branco);
  color: var(--cinza-300);
  cursor: pointer;
  transition: all 0.2s ease;
}

.aso-upload-face-card__modal-btn:hover:not(:disabled) {
  border-color: #8ac0f2;
  background-color: #f3f9ff;
}

.aso-upload-face-card__modal-btn--primary {
  background-color: var(--azul-50);
  color: var(--branco);
  border-color: var(--azul-50);
}

.aso-upload-face-card__modal-btn--primary:hover:not(:disabled) {
  background-color: var(--azul-100);
  border-color: var(--azul-100);
}

.aso-upload-face-card__modal-btn--danger {
  background-color: var(--vermelho);
  color: var(--branco);
  border-color: var(--vermelho);
}

.aso-upload-face-card__modal-btn--danger:hover:not(:disabled) {
  background-color: #d32f2f;
  border-color: #d32f2f;
}

.aso-upload-face-card__modal-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.aso-upload-face-card__camera-error {
  color: var(--vermelho);
  font-size: 13px;
  margin: 12px 16px;
  padding: 12px;
  background-color: #fff1f0;
  border-radius: 6px;
  border-left: 3px solid var(--vermelho);
}

.aso-upload-face-card__camera-error--inline {
  margin: 4px auto 0;
  max-width: 320px;
}

/* Modal title */
.aso-upload-face-card__modal-title {
  margin: 0;
  padding: 16px 16px 0 16px;
  font-size: 18px;
  font-weight: 700;
  color: #1a1a2e;
  text-align: center;
}

/* Preview modal styles */
.aso-upload-face-card__modal--preview {
  background-color: rgba(0, 0, 0, 0.95);
}

.aso-upload-face-card__modal--camera {
  background-color: rgba(0, 0, 0, 0.95);
}

.aso-upload-face-card__modal-content--camera {
  width: auto;
  max-width: 90vw;
  max-height: 95vh;
}

.aso-upload-face-card__modal-content--camera .aso-upload-face-card__video {
  width: auto;
  max-width: min(86vw, 680px);
  max-height: 70vh;
  align-self: center;
  border-radius: 8px;
}

.aso-upload-face-card__modal-content--preview {
  width: auto;
  max-width: 90vw;
  max-height: 95vh;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.aso-upload-face-card__media-container,
.aso-upload-face-card__preview-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;
  padding: 20px 16px;
  min-height: 300px;
}

.aso-upload-face-card__preview-full {
  max-width: 100%;
  max-height: 100%;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.aso-upload-face-card__modal-actions--preview {
  padding-top: 12px;
  border-top: 1px solid var(--brancos-100);
  background-color: #f0f2f5;
}

.aso-upload-face-card__modal-actions--camera {
  border-top: 1px solid var(--brancos-100);
  background-color: #f0f2f5;
}
</style>



// WEBPACK FOOTER //
// UploadFaceCard.vue?64bffad3